<template>
  <div
    v-observe-visibility="visibilityOptions"
    class="a-top-brokers__wrapper"
    :class="dynamicBrokerLayoutClass"
  >
    <div class="a-top-brokers__title-disclaimer-sponsored-container">
      <h2 class="a-top-brokers__title subtitle-3">
        Top Brokers
      </h2>
      <div class="a-top-brokers__disclaimer-sponsored-container">
        <p class="a-top-brokers__sponsored-label text-small">
          Sponsored
        </p>
        <a-general-risk-warning
          :disclaimer-layout-ordering="$options.consts.disclaimerLayoutOrdering"
          :disclaimer-popup-settings-by-breakpoint="
            disclaimerPopupSettingsByBreakpoint
          "
          :icon-size="16"
          :disclaimer-top-space-offset="
            $options.consts.disclaimerTopSpaceOffset
          "
        />
      </div>
    </div>
    <div class="a-top-brokers__broker-items">
      <div
        v-for="(broker, index) in topBrokersToRender"
        :key="`${broker.Id}-${index}`"
      >
        <div
          class="a-top-brokers__broker-container"
          v-if="!isPlaceholder(broker)"
        >
          <div class="a-top-brokers__broker-image-wrapper">
            <a-image
              :image="broker.Logo"
              :url="broker.Url"
              :aspect-ratio="
                $options.consts.aspectRatios.ilDirectoryCompanyLogo
              "
              class="a-top-brokers__broker-image"
            />
          </div>
          <div class="a-top-brokers__broker-title-button">
            <p class="a-top-brokers__broker-title">
              {{ broker.CompanyName }}
            </p>
            <a-button
              :button-style="$options.consts.BUTTON_STYLE.PURPLE"
              :href="broker.Url"
              :open-in-new-tab="true"
              class="a-top-brokers__broker-button"
            >
              Open Account
            </a-button>
          </div>
        </div>
        <a-link v-else :to="broker.Url" aria-label="Go to Contact Us Page">
          <a-image
            :src="getPlaceholderImageUrl"
            class="a-top-brokers__placeholder-container"
          />
        </a-link>
      </div>
    </div>
  </div>
</template>

<script>
import { aspectRatios } from 'enums/aspectRatios'
import { LAYOUT as POPUP_LAYOUT } from 'shared/APopup/enums'
import { guid, getLinkToIlBucketFile } from '@/plugins/helper'
import { ROUTE } from 'enums/routes'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { BUTTON_STYLE } from 'shared/AButton/index'
import ALink from 'shared/ALink'
import mixins from '@/utils/mixins'
import AGeneralRiskWarning from '@/components/_shared/AGeneralRiskWarning/index.vue'

const FIRST_ROW_START = 0
const FIRST_ROW_END = 2
const SECOND_ROW_START = 3
const SECOND_ROW_END = 5

const fillWithPlaceholders = (items, start, end) => {
  const hasItemsInRange = items.some(
    ({ Order }) => Order >= start && Order <= end
  )
  if (!hasItemsInRange) return []

  const itemsMap = new Map(items.map(item => [item.Order, item]))

  return Array.from({ length: end - start + 1 }, (_, i) => {
    const order = start + i
    return itemsMap.get(order) ?? createPlaceholder(order)
  })
}

const createPlaceholder = order => ({
  Id: guid(),
  Url: ROUTE.CONTACT_US,
  Order: order,
  IsPlaceholder: true,
  Logo: {
    MediaId: guid(),
    ModifiedOn: '0000-00-00T00:00:00.000Z',
    MediaType: 'Image',
    Title: 'Promote Your Brokerage Here',
    Caption: 'Promote Your Brokerage Here',
    AltText: 'Promote Your Brokerage Here',
    IsOld: false
  }
})

export default {
  name: 'ATopBrokers',
  mixins: [mixins.lazyHydratedComponentState],
  components: {
    AGeneralRiskWarning,
    ALink
  },
  props: {
    sidebarLayout: propValidator([PROP_TYPES.BOOLEAN], false, false),
    topBrokers: propValidator([PROP_TYPES.ARRAY], false, () => [])
  },
  consts: {
    aspectRatios,
    disclaimerLayoutOrdering: [
      POPUP_LAYOUT.BOTTOM_LEFT,
      POPUP_LAYOUT.TOP_LEFT,
      POPUP_LAYOUT.TOP_RIGHT,
      POPUP_LAYOUT.BOTTOM_RIGHT
    ],
    disclaimerTopSpaceOffset: -20,
    BUTTON_STYLE
  },
  data() {
    return {
      visibilityOptions: {
        callback: this.onVisibilityStateChange,
        once: true
      },
      disclaimerPopupSettingsByBreakpoint: {
        [this.$breakpoint.aboveDesktopXl]: {
          modalWidth: 244,
          modalHeight: 216
        },
        [this.$breakpoint.mobile]: {
          modalWidth: 244,
          modalHeight: 216
        }
      },
      isPlaceholderImageVisible: false
    }
  },
  computed: {
    topBrokersToRender() {
      return [
        ...fillWithPlaceholders(
          this.topBrokers,
          FIRST_ROW_START,
          FIRST_ROW_END
        ),
        ...fillWithPlaceholders(
          this.topBrokers,
          SECOND_ROW_START,
          SECOND_ROW_END
        )
      ]
    },
    dynamicBrokerLayoutClass() {
      if (this.$helper.isAmpPage(this.$route.name)) {
        return 'a-top-brokers__wrapper--amp'
      } else {
        return {
          'a-top-brokers__wrapper--homepage': !this.sidebarLayout,
          'a-top-brokers__wrapper--sidebar': this.sidebarLayout
        }
      }
    },
    getPlaceholderImageUrl() {
      return this.$helper.isAmpPage(this.$route.name) ||
        this.isPlaceholderImageVisible
        ? getLinkToIlBucketFile('top-broker-placeholder-il-v3.jpg')
        : null
    }
  },
  methods: {
    isPlaceholder(broker) {
      return broker.IsPlaceholder
    },
    onVisibilityStateChange(isVisible) {
      if (!isVisible) return

      this.showPlaceholderBackgroundImages()
    },
    showPlaceholderBackgroundImages() {
      this.isPlaceholderImageVisible = true
    }
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>

<style lang="scss" scoped>
$disclaimer-sponsored-color: $c--gray;
.a-top-brokers__wrapper--amp {
  margin-top: 32px;

  .a-top-brokers__broker-items {
    grid-template-columns: repeat(2, 1fr);
  }
}

.a-top-brokers__wrapper--homepage {
  .a-top-brokers__broker-items {
    grid-template-columns: repeat(3, 1fr);

    @include desktop-sm {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.a-top-brokers__wrapper--homepage,
.a-top-brokers__wrapper--amp {
  background: $c--gray-soft;
  border-radius: 6px;
  width: auto;
  padding: 24px;

  .a-top-brokers__title-disclaimer-sponsored-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    @include mobile {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .a-top-brokers__broker-items {
    display: grid;
    column-gap: 18px;
    row-gap: 12px;

    @include mobile {
      display: flex;
      flex-direction: column;
    }
  }

  .a-top-brokers__broker-container {
    transition: none;
    padding: 16px;
    border-radius: 6px;
    border: 1px solid $c--gray-light;
    overflow: hidden;
    background: $c--white;
    text-align: center;
    display: flex;

    @include mobile {
      flex-direction: column;
      align-items: center;
    }
  }

  .a-top-brokers__broker-container {
    .a-top-brokers__broker-image-wrapper {
      width: 148px;
      margin-right: 12px;

      @include mobile {
        margin-right: 0;
        margin-bottom: 8px;
      }
    }
  }

  .a-top-brokers__placeholder-container {
    min-height: 105px;
  }

  .a-top-brokers__broker-image-wrapper {
    width: 148px;
  }

  .a-top-brokers__broker-title-button {
    text-align: left;

    @include mobile {
      text-align: center;
    }
  }
}

.a-top-brokers__wrapper--sidebar {
  background: $c--gray-soft;
  border-radius: 6px;
  width: auto;
  padding: 24px;

  .a-top-brokers__title-disclaimer-sponsored-container {
    margin-bottom: 12px;
  }

  .a-top-brokers__broker-items {
    margin-top: 12px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .a-top-brokers__broker-container {
    display: block;
    transition: none;
    border-radius: 6px;
    padding: 16px;
    border: 1px solid $c--gray-light;
    overflow: hidden;
    background: $c--white;
    text-align: center;
  }

  .a-top-brokers__broker-image-wrapper {
    width: 148px;
    margin: 0 auto 8px;
  }

  .a-top-brokers__placeholder-container {
    min-height: 66px;
  }
}

.a-top-brokers__wrapper {
  .a-top-brokers__title {
    color: $c--black;
  }

  .a-top-brokers__disclaimer-sponsored-container {
    margin-top: 12px;
    display: flex;
    align-items: center;

    /deep/ .activator__wrapper {
      line-height: 10px;
    }
  }

  .a-top-brokers__sponsored-label {
    margin-right: 12px;
    color: $c--gray;
  }

  .a-top-brokers__placeholder-container {
    border-radius: 6px;
    border: 1px solid $c--gray-light;
    position: relative;
    width: 100%;
    overflow: hidden;

    /deep/ .a-image__image {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  .a-top-brokers__broker-image {
    border-radius: 4px;
  }

  .a-top-brokers__broker-title {
    font-size: 20px;
    font-weight: 500;
    line-height: 26px;

    @include tablet {
      font-size: 18px;
      line-height: 24px;
    }
  }

  .a-top-brokers__broker-button {
    margin: 6px auto 0;
    width: 130px;
    font-size: 14px;
  }
}
</style>
